/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * Draft editor: program details + the list of goals. Only rendered while the
 * program is a draft (the DB also blocks goal edits off-draft). Goals manage
 * their own persistence (GoalRow); this owns the program-detail fields and the
 * add/remove of goal rows.
 *
 * Field layout follows the UI design: grouped sections, paired 2-up rows. The
 * target state(s) come first and drive the distributor dropdown (distributors are
 * sourced from the company's States & Distributors reference data in System
 * Settings, read here through /shared — never a cross-module import).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { updateProgram, getIncentiveAccountId } from "../utils/incentiveService.js";
import { listSalesStates, listProducts } from "../../../shared/utils/referenceDataService.js";
import { listOutletsForPicker } from "../utils/depletionsContract.js";
import GoalRow, { blankGoal } from "./GoalRow.jsx";
import StatesMultiSelect from "./StatesMultiSelect.jsx";

export default function DraftEditor({ program, goals, onChanged }) {
  const [form, setForm] = useState({
    name: program.name || "",
    target_states: Array.isArray(program.target_states) ? program.target_states : [],
    distributor_name: program.distributor_name || "",
    brand_manager_name: program.brand_manager_name || "",
    brand_manager_email: program.brand_manager_email || "",
    start_date: program.start_date || "",
    end_date: program.end_date || "",
    notes: program.notes || "",
  });
  const [saveState, setSaveState] = useState("saved"); // 'saved' | 'saving' | 'error'
  const [err, setErr] = useState(null);

  // Company reference data: states + their distributors (System Settings owns it).
  const [salesStates, setSalesStates] = useState([]);
  useEffect(() => {
    let alive = true;
    listSalesStates().then((rows) => { if (alive) setSalesStates(rows || []); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const stateOptions = useMemo(
    () => [...new Set(salesStates.filter((s) => s.active !== false).map((s) => s.state_name).filter(Boolean))].sort(),
    [salesStates]
  );

  // Distributors that operate in the selected states (union, de-duped). Keep the
  // currently-saved distributor pinned even if the states change, so it's never
  // silently dropped.
  const distributorOptions = useMemo(() => {
    const chosen = new Set(form.target_states);
    const set = new Set(
      salesStates
        .filter((s) => chosen.has(s.state_name) && s.distributor)
        .map((s) => s.distributor)
    );
    if (form.distributor_name) set.add(form.distributor_name);
    return [...set].sort();
  }, [salesStates, form.target_states, form.distributor_name]);

  // ── Goal-scope option lists (item 233) ─────────────────────────────────────
  // SKUs come from the tenant's Product SKUs through /shared (System Settings
  // owns the table). Accounts come from the depletions RPC contract — the doors
  // this distributor has actually sold through — so the picker offers real
  // accounts instead of a free-text box nobody could reconcile at scoring time.
  const [productOptions, setProductOptions] = useState([]);
  useEffect(() => {
    let alive = true;
    listProducts()
      .then((rows) => {
        if (!alive) return;
        setProductOptions(
          (rows || [])
            .filter((p) => p.active !== false)
            .map((p) => ({
              id: p.id,
              label: p.name,
              sub: [p.flavor?.name, p.size?.abbreviation || p.size?.name, p.pack_size?.name].filter(Boolean).join(" · "),
              snapshot: { id: p.id, name: p.name },
            })),
        );
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const [accountOptions, setAccountOptions] = useState([]);
  const [accountsNote, setAccountsNote] = useState("");
  const distributorKey = form.distributor_name;
  useEffect(() => {
    let alive = true;
    if (!distributorKey) {
      setAccountOptions([]);
      setAccountsNote("Choose a target distributor to list its accounts.");
      return () => { alive = false; };
    }
    (async () => {
      try {
        const accountId = await getIncentiveAccountId();
        const res = await listOutletsForPicker({
          accountId, distributor: distributorKey,
          start: form.start_date || null, end: form.end_date || null,
        });
        if (!alive) return;
        if (!res.ok) {
          setAccountOptions([]);
          setAccountsNote("Accounts are listed from your depletion reports, which aren't available on this workspace.");
          return;
        }
        setAccountOptions(res.outlets.map((o) => ({
          id: o.id,
          label: o.name,
          sub: [o.chain, [o.city, o.state].filter(Boolean).join(", ")].filter(Boolean).join(" · "),
          snapshot: { id: o.id, name: o.name, chain: o.chain, city: o.city, state: o.state },
        })));
        setAccountsNote(res.outlets.length === 0
          ? `No accounts have reported depletions through ${distributorKey} yet.`
          : "");
      } catch {
        if (alive) { setAccountOptions([]); setAccountsNote("Couldn't load accounts just now."); }
      }
    })();
    return () => { alive = false; };
  }, [distributorKey, form.start_date, form.end_date]);

  const noStates = form.target_states.length === 0;
  const distributorPlaceholder = noStates
    ? "Choose target state(s) first"
    : distributorOptions.length === 0
      ? "No distributors found in the selected state(s)"
      : "Select a distributor…";

  // Local goal rows with stable keys (saved goals keyed by id; new blanks by tmp).
  const tmpId = useRef(0);
  const [rows, setRows] = useState(() => goals.map((g) => ({ key: g.id, goal: g })));

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  // Autosave program details (debounced) — saves as the user types, no button.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) { firstRun.current = false; return; }
    setSaveState("saving");
    const t = setTimeout(async () => {
      try {
        await updateProgram(program.id, form);
        setSaveState("saved"); setErr(null);
        onChanged?.();
      } catch (e) { setErr(e.message); setSaveState("error"); }
    }, 700);
    return () => clearTimeout(t);
    // onChanged is stable (parent useCallback); intentionally not a dep.
  }, [form, program.id]); // eslint-disable-line react-hooks/exhaustive-deps

  function addGoal() {
    const key = `tmp-${tmpId.current++}`;
    setRows((r) => [...r, { key, goal: { ...blankGoal(), sort_order: r.length } }]);
  }
  function removeRow(key) {
    setRows((r) => r.filter((x) => x.key !== key));
    onChanged?.();
  }

  return (
    <div>
      {/* ── Program ─────────────────────────────────────────────── */}
      <h2 style={{ marginTop: 0 }}>Program</h2>
      <div className="it-goal-grid">
        <label className="it-span2">
          Program name
          <input type="text" value={form.name} onChange={(e) => set({ name: e.target.value })}
                 placeholder="Q3 Illinois distribution push" />
        </label>
      </div>

      <hr className="it-divider" />

      {/* ── Targeting (states drive the distributor list) ───────── */}
      <h2 style={{ marginTop: 0 }}>Targeting</h2>
      <div className="it-goal-grid">
        <div className="it-field">
          <span>Target state(s) <span className="muted">(pick one or more)</span></span>
          <StatesMultiSelect
            options={stateOptions}
            value={form.target_states}
            onChange={(next) => set({ target_states: next })}
            placeholder="Select states…"
          />
          <span className="muted" style={{ fontSize: 12 }}>Distributors are filtered to the states you choose.</span>
        </div>
        <label>
          Target distributor
          <select
            value={form.distributor_name}
            disabled={noStates || distributorOptions.length === 0}
            onChange={(e) => set({ distributor_name: e.target.value })}
          >
            <option value="">{distributorPlaceholder}</option>
            {distributorOptions.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </label>
      </div>

      <hr className="it-divider" />

      {/* ── Brand manager (recipient) ───────────────────────────── */}
      <h2 style={{ marginTop: 0 }}>Brand manager</h2>
      <div className="it-goal-grid">
        <label>
          Brand manager name
          <input type="text" value={form.brand_manager_name} onChange={(e) => set({ brand_manager_name: e.target.value })} placeholder="Name" />
        </label>
        <label>
          Brand manager email
          <input type="email" value={form.brand_manager_email} onChange={(e) => set({ brand_manager_email: e.target.value })} placeholder="name@distributor.com" />
        </label>
      </div>

      <hr className="it-divider" />

      {/* ── Program window ──────────────────────────────────────── */}
      <h2 style={{ marginTop: 0 }}>Program window</h2>
      <div className="it-goal-grid">
        <label>
          Start date
          <input type="date" value={form.start_date || ""} onChange={(e) => set({ start_date: e.target.value })} />
        </label>
        <label>
          End date
          <input type="date" value={form.end_date || ""} onChange={(e) => set({ end_date: e.target.value })} />
        </label>
      </div>

      <hr className="it-divider" />

      {/* ── Notes ───────────────────────────────────────────────── */}
      <h2 style={{ marginTop: 0 }}>Notes <span className="muted" style={{ fontSize: 13, fontWeight: 400 }}>(internal only)</span></h2>
      <div className="it-goal-grid">
        <label className="it-span2">
          <textarea rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} placeholder="Anything your team should know — not shown to the distributor." />
        </label>
      </div>

      {err && <p className="error" style={{ fontSize: 13 }}>{err}</p>}
      <p className="muted" style={{ fontSize: 12, margin: "4px 0 0" }}>
        {saveState === "saving" ? "Saving…" : saveState === "error" ? "Couldn't save — retrying on next change." : "All changes saved automatically."}
      </p>

      <hr className="it-divider" />

      {/* ── Goals ───────────────────────────────────────────────── */}
      <div className="section-row">
        <h2 style={{ margin: 0 }}>Goals</h2>
        <span className="caption">{rows.length} goal{rows.length === 1 ? "" : "s"}</span>
      </div>

      {rows.length === 0 && <p className="muted">No goals yet — add the first one.</p>}
      {rows.map((r, i) => (
        <GoalRow
          key={r.key}
          programId={program.id}
          goal={r.goal}
          index={i}
          onSaved={onChanged}
          onRemoved={() => removeRow(r.key)}
          accountOptions={accountOptions}
          productOptions={productOptions}
          accountsNote={accountsNote}
        />
      ))}

      <button className="secondary" onClick={addGoal} style={{ marginTop: 4 }}>+ Add goal</button>
    </div>
  );
}
